/**
 * Trading Reports API Service
 * Handles all API calls for trading report preferences, Telegram linking, and report history
 */

import { apiClient } from './api';

// ============================================================================
// Types
// ============================================================================

export interface ReportPreferences {
  user_id: string;
  daily_enabled: boolean;
  weekly_enabled: boolean;
  monthly_enabled: boolean;
  email_enabled: boolean;
  telegram_enabled: boolean;
  telegram_chat_id?: string | null;
  telegram_username?: string | null;
  telegram_linked_at?: string | null;
  daily_send_hour: number;
  weekly_send_day: number;
  weekly_send_hour: number;
  monthly_send_day: number;
  monthly_send_hour: number;
  include_charts: boolean;
  top_strategies_count: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateReportPreferencesRequest {
  daily_enabled?: boolean;
  weekly_enabled?: boolean;
  monthly_enabled?: boolean;
  email_enabled?: boolean;
  telegram_enabled?: boolean;
  daily_send_hour?: number;
  weekly_send_day?: number;
  weekly_send_hour?: number;
  monthly_send_day?: number;
  monthly_send_hour?: number;
  include_charts?: boolean;
  top_strategies_count?: number;
}

export interface TelegramLinkCodeResponse {
  link_code: string;
  bot_username: string;
  deep_link: string;
  expires_in_seconds: number;
}

export interface CheckTelegramLinkRequest {
  link_code: string;
}

export interface CheckTelegramLinkResponse {
  success: boolean;
  message: string;
  telegram_username?: string | null;
  linked_at?: string | null;
}

export interface ReportData {
  report_type: string;
  period_start: string;
  period_end: string;
  period_start_short: string;
  period_end_short: string;
  human_friendly_period: string;
  has_activity: boolean;
  total_pnl: number;
  total_pnl_pct: number;
  trades_opened: number;
  trades_closed: number;
  wins: number;
  losses: number;
  win_rate: number;
  profit_factor: number;
  accounts: Array<{
    account: string;
    trade_count: number;
    wins: number;
    losses: number;
    win_rate: number;
    pnl: number;
    pnl_pct: number;
  }>;
  top_strategies: Array<{
    name: string;
    trade_count: number;
    pnl: number;
    pnl_pct: number;
  }>;
  closed_trades: Array<{
    symbol: string;
    side: string;
    account: string;
    strategy: string;
    pnl: number;
    closed_at: string;
  }>;
  opened_trades: Array<{
    symbol: string;
    side: string;
    account: string;
    strategy: string;
    opened_at: string;
  }>;
  has_open_positions: boolean;
  open_positions_count: number;
  open_positions_total_unrealized_pnl: number;
  open_positions: Array<{
    symbol: string;
    side: string;
    size: number;
    avgPrice: number;
    markPrice: number;
    unrealisedPnl: number;
    leverage: number;
    positionValue: number;
    accountname: string;
    strategy: string;
    duration_hours: number;
    stopLoss?: number;
    takeProfit?: number;
  }>;
}

export interface ReportHistory {
  id: number;
  report_type: 'daily' | 'weekly' | 'monthly' | 'test';
  period_start: string;
  period_end: string;
  sent_via_email: boolean;
  sent_via_telegram: boolean;
  email_sent_at?: string | null;
  telegram_sent_at?: string | null;
  email_opened: boolean;
  email_clicked: boolean;
  email_opened_at?: string | null;
  email_clicked_at?: string | null;
  email_error?: string | null;
  telegram_error?: string | null;
  report_data?: ReportData | null;
  generated_at: string;
}

export interface ReportHistoryResponse {
  total: number;
  reports: ReportHistory[];
}

export interface SendTestReportRequest {
  report_type: 'daily' | 'weekly' | 'monthly';
}

export interface SendTestReportResponse {
  success: boolean;
  message: string;
  email_sent: boolean;
  telegram_sent: boolean;
  report_history_id: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get user's trading report preferences
 */
export const getReportPreferences = async (): Promise<ReportPreferences> => {
  const response = await apiClient.get<ReportPreferences>('/reports/preferences');
  return response.data;
};

/**
 * Update user's trading report preferences
 */
export const updateReportPreferences = async (
  updates: UpdateReportPreferencesRequest
): Promise<ReportPreferences> => {
  const response = await apiClient.put<ReportPreferences>(
    '/reports/preferences',
    updates
  );
  return response.data;
};

/**
 * Generate Telegram link code
 */
export const generateTelegramLinkCode = async (): Promise<TelegramLinkCodeResponse> => {
  const response = await apiClient.post<TelegramLinkCodeResponse>(
    '/reports/telegram/link-code'
  );
  return response.data;
};

/**
 * Check Telegram link status (for polling)
 */
export const checkTelegramLink = async (
  linkCode: string
): Promise<CheckTelegramLinkResponse> => {
  const response = await apiClient.post<CheckTelegramLinkResponse>(
    '/reports/telegram/link',
    { link_code: linkCode }
  );
  return response.data;
};

/**
 * Unlink Telegram account
 */
export const unlinkTelegram = async (): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(
    '/reports/telegram/unlink'
  );
  return response.data;
};

/**
 * Get report delivery history
 */
export const getReportHistory = async (
  limit: number = 10,
  offset: number = 0
): Promise<ReportHistoryResponse> => {
  const response = await apiClient.get<ReportHistoryResponse>(
    `/reports/history?limit=${limit}&offset=${offset}`
  );
  return response.data;
};

/**
 * Send a test report
 */
export const sendTestReport = async (
  reportType: 'daily' | 'weekly' | 'monthly'
): Promise<SendTestReportResponse> => {
  const response = await apiClient.post<SendTestReportResponse>(
    '/reports/send-test',
    { report_type: reportType }
  );
  return response.data;
};
